import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Briefcase, Sparkles, CreditCard, FileText, ArrowUpRight } from "lucide-react";

const inr = (paise) => "₹" + (paise / 100).toLocaleString("en-IN");

export default function Dashboard() {
  const { user } = useAuth();
  const [apps, setApps] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/applications").then((r) => setApps(r.data)).catch(() => {}),
      api.get("/payments/orders").then((r) => setOrders(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  return (
    <div className="bg-cream min-h-screen pt-[72px]" data-testid="dashboard-page">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-14 lg:pt-20 pb-28">
        <span className="overline text-cobalt">Client portal</span>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-navy mt-4">
          Welcome, {user?.name?.split(" ")[0] || "there"}.
        </h1>

        {/* Quick actions */}
        <div className="grid md:grid-cols-3 gap-4 mt-10">
          <ActionCard to="/jobs" icon={Briefcase} title="Browse jobs" desc="Curated roles for your level." testid="dash-action-jobs" />
          <ActionCard to="/resume-review" icon={Sparkles} title="AI resume review" desc="Get instant, senior-grade feedback." testid="dash-action-review" />
          <ActionCard to="/pricing" icon={CreditCard} title="Buy a service" desc="Pay securely for a package or deposit." testid="dash-action-pay" />
        </div>

        {/* Orders */}
        <div className="mt-16">
          <h2 className="font-display text-2xl font-semibold text-navy mb-5">Your orders</h2>
          {loading ? (
            <p className="text-slateink">Loading…</p>
          ) : orders.length === 0 ? (
            <div className="bg-white border border-border p-10 text-center" data-testid="dash-orders-empty">
              <p className="text-slateink">No purchases yet.</p>
              <Link to="/pricing" className="inline-block mt-3 text-cobalt font-medium hover:underline">View pricing →</Link>
            </div>
          ) : (
            <div className="bg-white border border-border divide-y divide-border" data-testid="dash-orders">
              {orders.map((o) => (
                <div key={o.id} className="flex items-center justify-between gap-4 p-5" data-testid={`order-${o.id}`}>
                  <div>
                    <p className="font-display text-lg font-semibold text-navy">{o.item}</p>
                    <p className="text-sm text-slateink">{inr(o.amount)} · {new Date(o.created_at).toLocaleDateString()}</p>
                  </div>
                  <span className={`font-mono-ui text-[0.6rem] uppercase tracking-widest px-3 py-1.5 ${o.status === "paid" ? "bg-cobalt text-white" : "bg-secondary text-navy"}`}>{o.status}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Applications */}
        <div className="mt-16">
          <h2 className="font-display text-2xl font-semibold text-navy mb-5">Your applications</h2>
          {loading ? (
            <p className="text-slateink" data-testid="dash-loading">Loading…</p>
          ) : apps.length === 0 ? (
            <div className="bg-white border border-border p-10 text-center" data-testid="dash-empty">
              <p className="text-slateink">No applications yet.</p>
              <Link to="/jobs" className="inline-block mt-3 text-cobalt font-medium hover:underline">Explore opportunities →</Link>
            </div>
          ) : (
            <div className="bg-white border border-border divide-y divide-border" data-testid="dash-applications">
              {apps.map((a) => (
                <div key={a.id} className="flex items-center justify-between gap-4 p-5" data-testid={`application-${a.id}`}>
                  <div>
                    <p className="font-display text-lg font-semibold text-navy">{a.job_title}</p>
                    <p className="text-sm text-slateink">{a.company} · {new Date(a.created_at).toLocaleDateString()}</p>
                  </div>
                  <span className="font-mono-ui text-[0.6rem] uppercase tracking-widest bg-secondary text-navy px-3 py-1.5">{a.status}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {user?.role === "admin" && (
          <div className="mt-14">
            <Link to="/admin" data-testid="dash-admin-link"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-navy text-white font-medium hover:bg-cobalt transition-colors">
              Open admin — view leads <ArrowUpRight size={18} />
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

function ActionCard({ to, icon: Icon, title, desc, testid }) {
  return (
    <Link to={to} data-testid={testid}
      className="group bg-white border border-border p-6 hover:border-navy transition-colors">
      <Icon size={26} className="text-cobalt" strokeWidth={1.6} />
      <p className="font-display text-xl font-semibold text-navy mt-5">{title}</p>
      <p className="text-sm text-slateink mt-1">{desc}</p>
    </Link>
  );
}
